import React from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import { Link as RouterLink } from 'react-router-dom';
import { Box,
  Container,
  Heading,
  Text,
  VStack,
  Button,
  Icon,
  Separator,
  List,
} from '@chakra-ui/react';
import { FaArrowLeft } from 'react-icons/fa';
import Footer from './Footer';
import SEO from './SEO.jsx';

const TerminosDeUso = () => {
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');

  return (
    <Box minH="100vh" display="flex" flexDirection="column">
      <SEO
        title="Términos de Uso"
        description="Términos y condiciones de uso de la plataforma Cadencya."
        path="/terminos-de-uso"
      />
      {/* Header */}
      <Box bg="purple.600" color="white" py={6}>
        <Container maxW="7xl">
          <Button
            variant="ghost"
            color="white"
            _hover={{ bg: 'purple.700' }}
            mb={4}
            asChild><RouterLink to="/"><Icon asChild><FaArrowLeft /></Icon>Volver al inicio
                                    </RouterLink></Button>
          <Heading size="2xl" mb={4}>
            Términos y Condiciones de Uso
          </Heading>
          <Text fontSize="md">
            Última actualización: 25 de febrero de 2026
          </Text>
        </Container>
      </Box>

      {/* Contenido principal */}
      <Box flex="1" bg={bgColor} py={16}>
        <Container maxW="5xl">
          <Box bg={cardBg} p={{ base: 6, md: 10 }} rounded="2xl" shadow="lg">
            <VStack gap={8} align="stretch">
              {/* 1. Información general */}
              <Box>
                <Heading size="lg" color="purple.600" mb={4}>
                  1. Información General
                </Heading>
                <Text color="gray.700" mb={3}>
                  El presente documento establece los Términos y Condiciones de Uso (en adelante, "Términos") que regulan el acceso y uso de la plataforma Cadencya (en adelante, "la Plataforma"), disponible en el dominio www.cadencya.com y sus subdominios.
                </Text>
                <Text color="gray.700" mb={3}>
                  <strong>Titular de la Plataforma:</strong> Cadencya<br />
                  <strong>Email de contacto:</strong> info@cadencya.com
                </Text>
                <Text color="gray.700">
                  El uso de la Plataforma atribuye la condición de usuario (en adelante, "el Usuario") e implica la aceptación plena y sin reservas de todas y cada una de las disposiciones incluidas en estos Términos en la versión publicada en el momento en que el Usuario acceda a la Plataforma.
                </Text>
              </Box>

              <Separator />

              {/* 2. Objeto */}
              <Box>
                <Heading size="lg" color="purple.600" mb={4}>
                  2. Objeto de la Plataforma
                </Heading>
                <Text color="gray.700" mb={3}>
                  Cadencya es una plataforma digital que tiene como objeto facilitar la conexión entre bandas de música y cofradías/hermandades para la organización de procesiones y eventos relacionados con la Semana Santa y tradiciones cofrades.
                </Text>
                <Text color="gray.700">
                  La Plataforma permite a los usuarios:
                </Text>
                <List.Root as='ul' gap={2} color="gray.700" ml={6} mt={2}>
                  <List.Item>Crear perfiles de banda de música o cofradía</List.Item>
                  <List.Item>Publicar y gestionar eventos procesionales</List.Item>
                  <List.Item>Buscar y contactar con bandas o cofradías</List.Item>
                  <List.Item>Negociar acuerdos y condiciones</List.Item>
                  <List.Item>Gestionar disponibilidad y calendario</List.Item>
                  <List.Item>Valorar y reseñar experiencias</List.Item>
                </List.Root>
              </Box>

              <Separator />

              {/* 3. Registro y cuentas de usuario */}
              <Box>
                <Heading size="lg" color="purple.600" mb={4}>
                  3. Registro y Cuentas de Usuario
                </Heading>
                <Text color="gray.700" mb={3}>
                  <strong>3.1. Requisitos:</strong> Para utilizar determinadas funcionalidades de la Plataforma, el Usuario deberá registrarse y crear una cuenta. El registro está disponible para:
                </Text>
                <List.Root as='ul' gap={2} color="gray.700" ml={6} mb={4}>
                  <List.Item>Bandas de música legalmente constituidas</List.Item>
                  <List.Item>Cofradías y hermandades legalmente constituidas</List.Item>
                  <List.Item>Representantes autorizados de las anteriores</List.Item>
                </List.Root>
                <Text color="gray.700" mb={3}>
                  <strong>3.2. Veracidad de la información:</strong> El Usuario se compromete a proporcionar información veraz, exacta, actual y completa durante el proceso de registro. El Usuario es responsable de mantener actualizada su información.
                </Text>
                <Text color="gray.700" mb={3}>
                  <strong>3.3. Seguridad de la cuenta:</strong> El Usuario es responsable de mantener la confidencialidad de sus credenciales de acceso y de todas las actividades que se realicen bajo su cuenta.
                </Text>
                <Text color="gray.700">
                  <strong>3.4. Prohibiciones:</strong> Queda prohibido crear múltiples cuentas, compartir credenciales, o utilizar la cuenta de otro usuario.
                </Text>
              </Box>

              <Separator />

              {/* 4. Uso de la plataforma */}
              <Box>
                <Heading size="lg" color="purple.600" mb={4}>
                  4. Uso de la Plataforma
                </Heading>
                <Text color="gray.700" mb={3}>
                  El Usuario se compromete a utilizar la Plataforma de conformidad con la ley, estos Términos, y las buenas costumbres. Queda expresamente prohibido:
                </Text>
                <List.Root as='ul' gap={2} color="gray.700" ml={6}>
                  <List.Item>Utilizar la Plataforma para fines ilícitos o no autorizados</List.Item>
                  <List.Item>Publicar contenido falso, engañoso, difamatorio u ofensivo</List.Item>
                  <List.Item>Realizar actos de competencia desleal</List.Item>
                  <List.Item>Realizar scraping, crawling o extracción automatizada de datos</List.Item>
                  <List.Item>Interferir con la seguridad o funcionamiento de la Plataforma</List.Item>
                  <List.Item>Suplantar la identidad de otra persona o entidad</List.Item>
                  <List.Item>Utilizar la Plataforma para spam o comunicaciones no solicitadas</List.Item>
                  <List.Item>Vulnerar derechos de propiedad intelectual o industrial</List.Item>
                </List.Root>
              </Box>

              <Separator />

              {/* 5. Acuerdos entre usuarios */}
              <Box>
                <Heading size="lg" color="purple.600" mb={4}>
                  5. Acuerdos Entre Usuarios
                </Heading>
                <Text color="gray.700" mb={3}>
                  <strong>5.1. Intermediación:</strong> Cadencya actúa únicamente como intermediaria para facilitar el contacto entre bandas y cofradías. La Plataforma NO es parte de los acuerdos alcanzados entre usuarios.
                </Text>
                <Text color="gray.700" mb={3}>
                  <strong>5.2. Responsabilidad:</strong> Los usuarios son los únicos responsables de:
                </Text>
                <List.Root as='ul' gap={2} color="gray.700" ml={6} mb={4}>
                  <List.Item>Negociar y acordar condiciones, precios y términos</List.Item>
                  <List.Item>Cumplir con los acuerdos alcanzados</List.Item>
                  <List.Item>Resolver cualquier disputa o incumplimiento</List.Item>
                  <List.Item>Cumplir con obligaciones fiscales y legales derivadas de sus acuerdos</List.Item>
                </List.Root>
                <Text color="gray.700">
                  <strong>5.3. Exención de responsabilidad:</strong> Cadencya no se hace responsable de incumplimientos, cancelaciones, disputas o cualquier daño derivado de los acuerdos entre usuarios.
                </Text>
              </Box>

              <Separator />

              {/* 6. Propiedad intelectual */}
              <Box>
                <Heading size="lg" color="purple.600" mb={4}>
                  6. Propiedad Intelectual
                </Heading>
                <Text color="gray.700" mb={3}>
                  <strong>6.1. Derechos de Cadencya:</strong> Todos los contenidos de la Plataforma (textos, imágenes, logos, diseño, software, etc.) son propiedad de Cadencya o de sus licenciantes y están protegidos por las leyes de propiedad intelectual e industrial.
                </Text>
                <Text color="gray.700" mb={3}>
                  <strong>6.2. Contenido del usuario:</strong> El Usuario conserva todos los derechos sobre el contenido que publique (fotografías, vídeos, textos, etc.). Al publicar contenido, el Usuario otorga a Cadencya una licencia mundial, no exclusiva, libre de regalías para usar, reproducir, distribuir y mostrar dicho contenido en la Plataforma.
                </Text>
                <Text color="gray.700">
                  <strong>6.3. Garantías:</strong> El Usuario garantiza que tiene todos los derechos necesarios sobre el contenido que publica y que su uso no infringe derechos de terceros.
                </Text>
              </Box>

              <Separator />

              {/* 7. Protección de datos */}
              <Box>
                <Heading size="lg" color="purple.600" mb={4}>
                  7. Protección de Datos Personales
                </Heading>
                <Text color="gray.700" mb={3}>
                  El tratamiento de datos personales se rige por nuestra{' '}
                  <Text color="purple.600" fontWeight="600" asChild><RouterLink to="/politica-privacidad">Política de Privacidad
                                      </RouterLink></Text>
                  , que cumple con el Reglamento General de Protección de Datos (RGPD) y la Ley Orgánica de Protección de Datos (LOPD).
                </Text>
              </Box>

              <Separator />

              {/* 8. Suspensión y cancelación */}
              <Box>
                <Heading size="lg" color="purple.600" mb={4}>
                  8. Suspensión y Cancelación de Cuentas
                </Heading>
                <Text color="gray.700" mb={3}>
                  Cadencya se reserva el derecho de suspender o cancelar cuentas de usuario en caso de:
                </Text>
                <List.Root as='ul' gap={2} color="gray.700" ml={6} mb={4}>
                  <List.Item>Incumplimiento de estos Términos</List.Item>
                  <List.Item>Conducta fraudulenta o ilegal</List.Item>
                  <List.Item>Uso abusivo de la Plataforma</List.Item>
                  <List.Item>Recepción de múltiples quejas o valoraciones negativas</List.Item>
                  <List.Item>Inactividad prolongada (más de 24 meses)</List.Item>
                </List.Root>
                <Text color="gray.700">
                  El Usuario puede cancelar su cuenta en cualquier momento desde su perfil.
                </Text>
              </Box>

              <Separator />

              {/* 9. Limitación de responsabilidad */}
              <Box>
                <Heading size="lg" color="purple.600" mb={4}>
                  9. Limitación de Responsabilidad
                </Heading>
                <Text color="gray.700" mb={3}>
                  Cadencya no garantiza:
                </Text>
                <List.Root as='ul' gap={2} color="gray.700" ml={6} mb={4}>
                  <List.Item>La disponibilidad ininterrumpida de la Plataforma</List.Item>
                  <List.Item>La ausencia de errores técnicos</List.Item>
                  <List.Item>La veracidad de la información publicada por usuarios</List.Item>
                  <List.Item>El cumplimiento de acuerdos entre usuarios</List.Item>
                </List.Root>
                <Text color="gray.700">
                  Cadencya no será responsable de daños directos, indirectos, lucro cesante o cualquier otro daño derivado del uso de la Plataforma, salvo en casos de dolo o negligencia grave.
                </Text>
              </Box>

              <Separator />

              {/* 10. Modificaciones */}
              <Box>
                <Heading size="lg" color="purple.600" mb={4}>
                  10. Modificación de los Términos
                </Heading>
                <Text color="gray.700">
                  Cadencya se reserva el derecho de modificar estos Términos en cualquier momento. Las modificaciones serán notificadas a los usuarios registrados y se publicarán en esta página con indicación de la fecha de última actualización. El uso continuado de la Plataforma tras la publicación de cambios constituye la aceptación de los nuevos Términos.
                </Text>
              </Box>

              <Separator />

              {/* 11. Ley aplicable y jurisdicción */}
              <Box>
                <Heading size="lg" color="purple.600" mb={4}>
                  11. Ley Aplicable y Jurisdicción
                </Heading>
                <Text color="gray.700" mb={3}>
                  Estos Términos se rigen por la legislación española.
                </Text>
                <Text color="gray.700">
                  Para la resolución de cualquier controversia derivada de estos Términos, las partes se someten a los Juzgados y Tribunales de Sevilla, España, renunciando expresamente a cualquier otro fuero que pudiera corresponderles.
                </Text>
              </Box>

              <Separator />

              {/* 12. Contacto */}
              <Box>
                <Heading size="lg" color="purple.600" mb={4}>
                  12. Contacto
                </Heading>
                <Text color="gray.700" mb={2}>
                  Para cualquier consulta relacionada con estos Términos, puede contactarnos en:
                </Text>
                <Text color="gray.700">
                  <strong>Email:</strong> info@cadencya.com
                </Text>
              </Box>

              {/* Nota final */}
              <Box bg="purple.50" p={6} rounded="xl" mt={8}>
                <Text fontSize="sm" color="gray.700" textAlign="center">
                  Al utilizar Cadencya, usted acepta quedar vinculado por estos Términos y Condiciones de Uso.
                  Le recomendamos que imprima o guarde una copia de este documento para su referencia.
                </Text>
              </Box>
            </VStack>
          </Box>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};

export default TerminosDeUso;
